import React, { useState, useEffect, useRef } from 'react'; // React хуки
import { createPortal } from 'react-dom'; // Portal для рендеринга выпадающего списка поверх всего
import { Loader2, X } from 'lucide-react'; // Иконки

// Интерфейс свойств (props) компонента
interface AutocompleteProps<T> {
  label?: string; // Текст над полем
  placeholder: string; // Подсказка внутри поля
  onSearch: (query: string) => Promise<T[]>; // Функция поиска (возвращает Promise)
  onSelect: (item: T | null) => void; // Функция обратного вызова при выборе элемента
  displayValue: (item: T) => string; // Функция для отображения названия элемента
  initialValue?: T | null; // Начальное значение
  className?: string; // CSS классы
}

// Универсальный (Generic) компонент автозаполнения
export const Autocomplete = <T,>({
  label,
  placeholder,
  onSearch,
  onSelect,
  displayValue,
  initialValue,
  className = "",
}: AutocompleteProps<T>) => {
  const [query, setQuery] = useState(''); // Текущий текст в поле ввода
  const [results, setResults] = useState<T[]>([]); // Найденные результаты
  const [isLoading, setIsLoading] = useState(false); // Индикатор загрузки
  const [isOpen, setIsOpen] = useState(false); // Открыт ли выпадающий список
  const [selectedItem, setSelectedItem] = useState<T | null>(initialValue || null); // Выбранный объект
  
  // Координаты для позиционирования выпадающего списка
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  // Refs для доступа к DOM элементам
  const wrapperRef = useRef<HTMLDivElement>(null); // Обертка всего компонента
  const inputRef = useRef<HTMLInputElement>(null); // Поле ввода
  const dropdownRef = useRef<HTMLDivElement>(null); // Выпадающий список (в портале)

  // Синхронизация: если initialValue изменилось извне, обновляем локальное состояние
  useEffect(() => {
    if (initialValue) {
      setSelectedItem(initialValue);
      setQuery(displayValue(initialValue));
    }
  }, [initialValue, displayValue]);

  // Функция для обновления позиции выпадающего списка относительно поля ввода
  const updatePosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect(); // Получаем координаты поля
      setCoords({
        top: rect.bottom + 4, // Список чуть ниже поля
        left: rect.left,
        width: rect.width // Ширина списка равна ширине поля
      });
    }
  };

  // Эффект для поиска с задержкой (Debounce)
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Если текст короткий или элемент уже выбран, не ищем
      if (query.length < 2 || selectedItem) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        // Выполняем поиск
        const data = await onSearch(query);
        setResults(data);
        if (data.length > 0) {
            updatePosition(); // Обновляем позицию перед открытием
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
      } catch (err) {
        console.error(err);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 400); // Задержка 400мс

    return () => clearTimeout(timer); // Очистка таймера при каждом новом вводе
  }, [query, onSearch, selectedItem]);

  // Эффект для слежения за скроллом и изменением размера окна (чтобы список "прилипал" к полю)
  useEffect(() => {
    if (isOpen) {
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        updatePosition();
    }
    return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Эффект закрытия списка при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      const clickedWrapper = wrapperRef.current && wrapperRef.current.contains(target);
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(target);

      if (!clickedWrapper && !clickedDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Обработчик выбора элемента из списка
  const handleSelect = (item: T) => {
    setSelectedItem(item);
    setQuery(displayValue(item)); // Устанавливаем текст поля равным названию элемента
    onSelect(item); // Вызываем колбэк родителя
    setIsOpen(false);
    setResults([]);
  };

  // Очистка выбора (крестик)
  const clearSelection = () => {
    setSelectedItem(null);
    setQuery('');
    onSelect(null);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {/* Лейбл */}
      {label && <label className="block mb-1 text-[11px] font-bold text-gray-500 uppercase">{label}</label>}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full p-2 border rounded text-sm focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4] transition-colors ${
            selectedItem ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-300'
          }`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Если пользователь начинает редактировать выбранное, сбрасываем выбор
            if (selectedItem && e.target.value !== displayValue(selectedItem)) {
              setSelectedItem(null); 
              onSelect(null);
            }
          }}
          onFocus={() => {
            // Открываем список при фокусе, если есть результаты
            if (results.length > 0 && !selectedItem) {
                updatePosition();
                setIsOpen(true);
            }
          }}
        />
        
        {/* Индикатор загрузки (спиннер) */}
        {isLoading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-[#0078d4]" />
          </div>
        )}

        {/* Кнопка очистки */}
        {!isLoading && selectedItem && (
          <button 
            onClick={clearSelection}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Рендеринг выпадающего списка через Portal в body */}
      {isOpen && results.length > 0 && createPortal(
        <div 
            ref={dropdownRef}
            style={{
                position: 'fixed', // Фиксированное позиционирование
                top: coords.top,
                left: coords.left,
                width: coords.width,
                zIndex: 9999 // Поверх всего
            }}
            className="bg-white border border-gray-200 rounded shadow-xl max-h-48 overflow-y-auto"
        >
          {results.map((item, idx) => (
            <div
              key={idx}
              className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-blue-50 border-b border-gray-50 last:border-none"
              onClick={() => handleSelect(item)}
            >
              {displayValue(item)}
            </div>
          ))}
        </div>,
        document.body // Целевой контейнер портала
      )}
    </div>
  );
};
